/**
 * @graphql cc
 */
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';

import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter';
import { useInlineCommentQueryParams } from '@confluence/comment';
import { CommentType } from '@confluence/comments-data';
import {
	checkElementExpandInEditor,
	scrollToElementWithViewingRoom,
	watchForCommentRepliesAndScroll,
} from '@confluence/comments-util';
import { withFlags, type WithFlagsProps } from '@confluence/flags';
import { getLogger } from '@confluence/logger';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { usePageContentId } from '@confluence/page-context';
import { ResolvedInlineCommentsDialogLoader } from '@confluence/resolved-inline-comments-dialog';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { useShowCommentsPanel } from '@confluence/comments-panel';
import { ViewValues, useCommentsPanelActions } from '@confluence/comments-panel-utils';
import { fg } from '@confluence/feature-gating';
import { useRendererActions } from '@confluence/renderer-actions';

import type {
	InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery as InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$data,
	InlineCommentQueryParamsListenerInlineCommentQueryParamsQueryVariables as InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$variables,
} from './__types__/InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery';
import type { InlineCommentQueryParamsQuery_comments_nodes_location_InlineComment as InlineCommentLocation } from './graphql/__types__/InlineCommentQueryParamsQuery';
import { useRetryOnMutation } from './useRetryOnMutation';

const InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery = gql`
	query InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery($commentId: ID!) {
		comments(commentId: $commentId) {
			nodes {
				id
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							resolved
						}
					}
				}
				ancestors {
					id
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineResolveProperties {
								resolved
							}
						}
					}
				}
				parentId
			}
		}
	}
`;
const logger = getLogger('inline-comments:query-param-listener');

export const i18n = defineMessages({
	commentLinkNotFoundFlagTitle: {
		id: 'comments-query-param-handlers.comment-link-not-found.flag.title',
		defaultMessage: 'This comment is not available',
		description:
			'The title for a warning message informing the user they clicked on a link to a comment that no longer exists because it is probably deleted.',
	},
	commentLinkNotFoundFlagDescription: {
		id: 'comments-query-param-handlers.comment-link-not-found.flag.description',
		defaultMessage: 'It may be deleted.',
		description:
			'A warning message informing the user they clicked on a link to a comment that no longer exists because it is probably deleted.',
	},
});

type InlineCommentQueryParamsListenerProps = {
	isArchived: boolean;
	rendererRef: React.RefObject<Element>;
};

type QueryParamData =
	| {
			location?: InlineCommentLocation;
			parentId?: string | null;
			parentLocation?: InlineCommentLocation;
	  }
	| undefined;

export const InlineCommentQueryParamsListenerComponent: React.FC<
	InlineCommentQueryParamsListenerProps & WithFlagsProps
> = ({ isArchived, rendererRef, flags }) => {
	const [isResolveDialogShown, setIsResolveDialogShown] = useState(false);
	const hasHandledCommentLink = useRef(false);
	const { showCommentsPanel } = useShowCommentsPanel();
	const { showModal } = useDialogs();
	const [contentId] = usePageContentId();
	const {
		focusedCommentId,
		replyToCommentId,
		editCommentId,
		removeCommentQueryParams,
		isCommentNotFound,
	} = useInlineCommentQueryParams({
		queryArchivedContent: isArchived,
	});

	const { rendererActions } = useRendererActions();

	const { setCurrentlySelectedThreadKey, setCommentContainerToFocus } = useCommentsPanelActions();
	const commentId = focusedCommentId || replyToCommentId || editCommentId;

	const { data, loading, error } = useQuery<
		InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$data,
		InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery$variables
	>(InlineCommentQueryParamsListenerInlineCommentQueryParamsQuery, {
		variables: {
			commentId: commentId!,
		},
		skip: !commentId,
	});
	if (error) {
		logger.error`An error occurred when querying the focused comment status; ${error}`;

		// TODO: If this query fails, there isn't much we can do, so just track it with experience tracker?

		markErrorAsHandled(error);
	}

	const queryParamData: QueryParamData = useMemo(() => {
		if (!error && !loading && data) {
			const location = data.comments?.nodes?.[0]?.location as InlineCommentLocation | undefined;
			const parentId = data.comments?.nodes?.[0]?.parentId;
			const parentLocation = data.comments?.nodes?.[0]?.ancestors?.[0]?.location as
				| InlineCommentLocation
				| undefined;

			return {
				location,
				parentId,
				parentLocation,
			};
		}

		return undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId, commentId, data, loading, error]);

	const queryParamDataExt = useMemo(() => {
		const { location, parentLocation } = queryParamData || {};
		return location?.inlineMarkerRef
			? {
					annotationId: location.inlineMarkerRef,
					isResolved: Boolean(location.inlineResolveProperties?.resolved),
				}
			: {
					annotationId: parentLocation?.inlineMarkerRef,
					isResolved: Boolean(parentLocation?.inlineResolveProperties?.resolved),
				};
	}, [queryParamData]);

	const tryToFocusAndScrollActiveCommentCallback = useCallback(() => {
		return tryToFocusAndScrollActiveComment({
			parentId: queryParamData?.parentId,
			annotationId: queryParamDataExt?.annotationId || null,
			commentId: commentId || null,
		});
	}, [queryParamData, queryParamDataExt, commentId]);

	useRetryOnMutation({
		callback: tryToFocusAndScrollActiveCommentCallback,
		elementRef: rendererRef,
		skip: !commentId || !queryParamData || queryParamDataExt.isResolved,
	});

	useEffect(() => {
		if (!queryParamData || hasHandledCommentLink.current) {
			return;
		}

		const { location, parentId } = queryParamData;
		const { annotationId } = queryParamDataExt;

		if (location) {
			if (queryParamDataExt.isResolved) {
				if (fg('confluence-frontend-comments-panel')) {
					showCommentsPanel({ openWithView: ViewValues.RESOLVED });
					if (annotationId) {
						setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE);
						setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
					}
				} else {
					if (!isResolveDialogShown) {
						setIsResolveDialogShown(true);
						// @ts-expect-error - Incompatible types but works fine
						showModal(ResolvedInlineCommentsDialogLoader, {
							context: 'renderer',
							contentId: contentId!,
							focusedCommentData: {
								id: commentId,
								parentCommentId: parentId,
							},
							onClose: () => {
								removeCommentQueryParams();
								setIsResolveDialogShown(false);
							},
						});
					}
				}
			} else {
				if (fg('confluence-frontend-comments-panel')) {
					// Check if annotation exists in renderer only when we need to make the decision
					const annotationExists = rendererActions
						? rendererActions
								.getAnnotationMarks()
								?.some((mark: any) => mark.attrs.id === annotationId)
						: false;

					if (
						annotationId &&
						!annotationExists &&
						fg('confluence_frontend_new_dangling_comments_ux')
					) {
						setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE); // needed so that the view knows which thread to scroll to after rendering the threads
						setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
						showCommentsPanel({ openWithView: ViewValues.DELETED_CONTENT });
					}
				}
			}
			hasHandledCommentLink.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryParamData, isResolveDialogShown, rendererActions]);

	// Only display the "comment not found" flag once
	useEffect(() => {
		if (isCommentNotFound && fg('confluence_frontend_comments_link_not_found_msg')) {
			const VIEWPAGE_COMMMENT_LINK_NOT_FOUND_FLAG_ID = `viewpage-comment-link-not-found-${commentId}`;
			void flags.showWarningFlag({
				id: VIEWPAGE_COMMMENT_LINK_NOT_FOUND_FLAG_ID,
				title: <FormattedMessage {...i18n.commentLinkNotFoundFlagTitle} />,
				description: <FormattedMessage {...i18n.commentLinkNotFoundFlagDescription} />,
			});
		}
	}, [commentId, flags, isCommentNotFound]);

	return null;
};

const tryToFocusAndScrollActiveComment = ({
	parentId,
	annotationId,
	commentId,
}: {
	parentId: string | null | undefined;
	annotationId: string | null;
	commentId: string | null;
}): boolean => {
	const mark = document.querySelector<HTMLElement>(`[data-id="${annotationId}"]`);

	if (mark) {
		let focusedMarkerRef = document.querySelector("[data-has-focus='true']");
		// If the already open container is showing the correct comment, don't send another click event to open it
		if (focusedMarkerRef?.id !== annotationId) {
			const emitter = getRendererAnnotationEventEmitter();

			setTimeout(() => {
				checkElementExpandInEditor(mark);
				// Excerpt(nested renderer) like macros re-render this component making the
				// highlight to be removed from the DOM
				// so, need to check the focus attribute once the ON_ANNOTATION_CLICK gets triggered
				focusedMarkerRef = document.querySelector("[data-has-focus='true']");
				if (
					!focusedMarkerRef &&
					emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
						annotationIds: [annotationId],
						eventTarget: mark,
					})
				) {
					emitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, {
						annotationId,
					});
				}
				// We need the timeout to give the document the chance to make the marks properly
			}, 1000);
		}

		// Using common utility from comments-util

		// if the comment is a reply, don't scroll to it here, we'll handle the reply scrolling after the reply has rendered to avoid double scrolling
		if (!parentId && !fg('confluence-fe-comments-deep-link-behavior')) {
			abortAll('new_interaction', `focused-comment-scroll`);
			// note that this is for Renderer for now
			scrollToElementWithViewingRoom({
				element: mark,
				useInstantScroll: true,
			});
		}

		// Only proceed if we have a valid commentId
		if (commentId && fg('confluence-fe-comments-deep-link-behavior')) {
			// Check if comment already exists in DOM
			const existingComment = document.querySelector(`[data-comment-id="${commentId}"]`);
			if (existingComment) {
				const showMoreButton = document.querySelector(
					'[data-testid="comments-panel-thread-show-hidden-replies"]',
				);
				const allRepliesVisible = !showMoreButton;

				if (allRepliesVisible) {
					existingComment.scrollIntoView({
						behavior: 'instant',
						block: 'center',
					});
				} else {
					abortAll('new_interaction', `focused-comment-scroll`);
					scrollToElementWithViewingRoom({
						element: mark,
						useInstantScroll: true,
					});
				}
			} else {
				// Watch for comment to be rendered using common utility
				watchForCommentRepliesAndScroll({
					targetCommentId: commentId,
					onScrollReady: (commentElement) => {
						commentElement.scrollIntoView({
							behavior: 'instant',
							block: 'center',
						});
					},
					onFallback: () => {
						abortAll('new_interaction', `focused-comment-scroll`);
						scrollToElementWithViewingRoom({
							element: mark,
							useInstantScroll: true,
						});
					},
					markElement: document.querySelector<HTMLElement>(`[data-id="${annotationId}"]`),
				});
			}
		}
	}

	return !!mark;
};

export const InlineCommentQueryParamsListener = withFlags(
	InlineCommentQueryParamsListenerComponent,
);
